import type { ChangeEvent } from 'react';

import Button from '@/componentsv2/Button';
import Input from '@/componentsv2/Input';

import Banner from './Banner';
import EmailPreview from './EmailPreview';

// The api's limits on an edited copy (MaxInvitationSubjectLength /
// MaxInvitationBodyLength), so the fields stop where the api would refuse.
export const INVITATION_SUBJECT_MAX_LENGTH = 255;
export const INVITATION_BODY_MAX_LENGTH = 10000;

/** A subject and body: templates while edited, rendered in a preview. */
export interface EmailDraft {
  subject: string;
  body: string;
}

export interface EmailPreviewState {
  /** The draft rendered for the chosen expert; null while the draft can't be sent. */
  email: EmailDraft | null;
  /** A newer render is on its way. */
  updating: boolean;
  /** The last render failed for a reason other than the draft. */
  failed: boolean;
}

/**
 * Where the editor stands against the draft Claude wrote when it prepared the
 * batch: still Claude's draft as written, edited, or reset to the standard email.
 */
export type ClaudeDraftState = 'unchanged' | 'edited' | 'standard';

/** The Send screen's draft of the invitation, as useInvitationDraft keeps it. */
export interface EmailEditorState {
  draft: EmailDraft;
  /** The draft differs from the standard email. */
  edited: boolean;
  /** Null when Claude drafted no email for this batch. */
  claudeDraft: ClaudeDraftState | null;
  /** Why the draft can't be sent, if it can't. */
  problem: string | null;
  preview: EmailPreviewState;
  /** The expert the preview is rendered for. */
  previewProfileId: string;
  onChange: (draft: EmailDraft) => void;
  onReset: () => void;
  /** Puts Claude's draft back in the fields. */
  onRestoreClaudeDraft: () => void;
  onPreviewProfileChange: (profileId: string) => void;
}

// The label over a draft Claude wrote, by where the preparer has taken it.
const CLAUDE_DRAFT_LABELS: Record<ClaudeDraftState, { title: string; text: string }> = {
  unchanged: {
    title: 'Drafted by Claude for your review',
    text: 'Claude wrote this email when it prepared these invitations. Read it before you send: it goes to every expert below, with the placeholders filled in for each one. Change anything you need to. Nothing is emailed until you click Send.',
  },
  edited: {
    title: "Claude's draft, edited by you",
    text: 'What the fields say now is what the experts will get.',
  },
  standard: {
    title: "Standard email, not Claude's draft",
    text: "You reset to the standard email. Restore Claude's draft to use it again.",
  },
};

interface Recipient {
  profile_id: string;
  profile_name: string;
}

interface EmailEditorProps {
  editor: EmailEditorState;
  recipients: Recipient[];
  /** True while Send or Cancel is in flight. */
  disabled?: boolean;
}

/**
 * The editable invitation of a pending batch: subject and body templates,
 * prefilled with Claude's draft when it wrote one and the standard email
 * otherwise, and the api's render of the draft for the expert chosen. The
 * edit goes only with Send; the unsubscribe footer is added whatever the body
 * says.
 */
export default function EmailEditor({
  editor,
  recipients,
  disabled,
}: EmailEditorProps): JSX.Element {
  const { draft, edited, claudeDraft, problem, preview, previewProfileId } = editor;
  const claudeLabel = claudeDraft ? CLAUDE_DRAFT_LABELS[claudeDraft] : null;
  const ownership = claudeDraft
    ? "Claude drafted this email but can't change or send it; only you can, here."
    : "Claude can't change this email; only you can, here.";
  const previewName = recipients.find((r) => r.profile_id === previewProfileId)?.profile_name ?? '';
  // With no render to show: the draft can't be sent, or its first render is on
  // its way. A failed render is said above, so it isn't called loading.
  let noPreview: string | null = null;
  if (problem) noPreview = 'Fix the email above to see the preview.';
  else if (!preview.failed) noPreview = 'Loading the preview…';

  function handleSubject(event: ChangeEvent<HTMLInputElement>) {
    editor.onChange({ ...draft, subject: event.target.value });
  }

  function handleBody(event: ChangeEvent<HTMLInputElement>) {
    editor.onChange({ ...draft, body: event.target.value });
  }

  return (
    <div className="flex flex-col gap-16">
      {claudeLabel ? (
        <Banner tone="info" title={claudeLabel.title} live={false}>
          {claudeLabel.text}
        </Banner>
      ) : null}
      <p className="m-[0] text-on-surface-muted body-2">
        You can edit the subject and body before sending. Placeholders such as [field:
        expert.first_name] are filled in for each expert, and the unsubscribe link is always added
        after the body. {ownership}
      </p>
      <Input
        label="Subject"
        value={draft.subject}
        onChange={handleSubject}
        disabled={disabled}
        fullWidth
        inputProps={{ maxLength: INVITATION_SUBJECT_MAX_LENGTH }}
      />
      <Input
        label="Body"
        value={draft.body}
        onChange={handleBody}
        disabled={disabled}
        fullWidth
        multiline
        minRows={12}
        inputProps={{ maxLength: INVITATION_BODY_MAX_LENGTH }}
      />
      {problem ? (
        <p role="alert" className="m-[0] text-alert-error body-2 dark:text-red-400">
          {problem}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-8">
        <Button
          variant="secondary"
          size="small"
          className="min-h-48"
          onClick={editor.onReset}
          disabled={disabled || !edited}
        >
          Reset to the standard email
        </Button>
        {claudeDraft && claudeDraft !== 'unchanged' ? (
          <Button
            variant="secondary"
            size="small"
            className="min-h-48"
            onClick={editor.onRestoreClaudeDraft}
            disabled={disabled}
          >
            Restore Claude&apos;s draft
          </Button>
        ) : null}
      </div>

      <h3 className="m-[0] font-semibold text-on-surface subtitle-2">Preview</h3>
      {recipients.length > 1 ? (
        <Input
          select
          label="Preview for"
          value={previewProfileId}
          onChange={(event) => editor.onPreviewProfileChange(event.target.value)}
          SelectProps={{ native: true }}
        >
          {recipients.map((recipient) => (
            <option key={recipient.profile_id} value={recipient.profile_id}>
              {recipient.profile_name}
            </option>
          ))}
        </Input>
      ) : null}
      <p className="m-[0] text-on-surface-muted body-2">
        Preview for {previewName}. Each expert&apos;s email greets them by name.
      </p>
      {preview.failed ? (
        <p className="m-[0] text-on-surface-muted body-2">
          The preview couldn&apos;t be updated. The email is checked again when you click Send.
        </p>
      ) : null}
      {preview.email ? (
        <EmailPreview
          subject={preview.email.subject}
          body={preview.email.body}
          updating={preview.updating}
        />
      ) : null}
      {!preview.email && noPreview ? (
        <p className="m-[0] text-on-surface-muted body-1">{noPreview}</p>
      ) : null}
    </div>
  );
}
